import { createSlice } from "@reduxjs/toolkit";
import {
  fetchRecordedCalls,
  getRecordingDownloadUrl,
  getRecordingPlaybackUrl,
  retainRecordedCall,
  releaseRecordedCall,
} from "./recordedCallsThunks";

const recordedCallsSlice = createSlice({
  name: "recordedCalls",
  initialState: {
    recordedCalls: [],
    total: 0,
    loading: false,
    error: null,
    // These hold the currently active filters and sort configuration.
    // For filters, you might store an array of objects like:
    // [{ field: 'firstName', operator: 'contains', value: 'John' }]
    filters: [],
    // For sortModel, an array like:
    // [{ field: 'firstName', sort: 'asc' }]
    sortModel: [],
    currentPage: 1,
    pageSize: 200, // how many contacts to load per request
    hasMore: true, // whether there are more contacts to load
    // playback and download states
    loadingPlayback: false,
    playbackError: null,
    loadingDownload: false,
    downloadError: null,
    downloadUrl: null,
    // retain and release state
  },
  reducers: {
    setFilters(state, action) {
      //   state.filters = action.payload; - chatgpt
      // append to state.filters, not set it
      state.filters = [...state.filters, ...action.payload];
      // When filters change, reset pagination
      state.currentPage = 1;
      state.recordedCalls = [];
      state.hasMore = true;
    },
    setSortModel(state, action) {
      state.sortModel = action.payload;
      // When sort changes, reset pagination
      state.currentPage = 1;
      state.recordedCalls = [];
      state.hasMore = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecordedCalls.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecordedCalls.fulfilled, (state, action) => {
        state.loading = false;
        const { data, page } = action.payload;

        // For the first page, replace the contacts; for later pages, append.
        const recordedCalls = data.data;
        if (page === 1) {
          state.recordedCalls = recordedCalls;
        } else {
          state.recordedCalls = [...state.recordedCalls, ...recordedCalls];
        }
        state.currentPage = page;
        state.hasMore = recordedCalls.length === state.pageSize;
      })
      .addCase(fetchRecordedCalls.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getRecordingPlaybackUrl.pending, (state) => {
        state.loadingPlayback = true;
        state.playbackError = null;
      })
      .addCase(getRecordingPlaybackUrl.fulfilled, (state) => {
        state.loadingPlayback = false;
      })
      .addCase(getRecordingPlaybackUrl.rejected, (state, action) => {
        state.loadingPlayback = false;
        state.playbackError = action.payload;
      })
      // getRecordingDownloadUrl
      .addCase(getRecordingDownloadUrl.pending, (state) => {
        state.loadingDownload = true;
        state.downloadError = null;
        state.downloadUrl = null;
      })
      .addCase(getRecordingDownloadUrl.fulfilled, (state, action) => {
        state.loadingDownload = false;
        state.downloadUrl = action.payload;
      })
      .addCase(getRecordingDownloadUrl.rejected, (state, action) => {
        state.loadingDownload = false;
        state.downloadError = action.payload;
      })
      .addCase(retainRecordedCall.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(retainRecordedCall.fulfilled, (state, action) => {
        state.loading = false;
        const call = state.recordedCalls.find(
          (c) => c.id === action.payload.recordedCallId
        );
        if (call) {
          call.retainPermanently = true;
        }
      })
      .addCase(retainRecordedCall.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(releaseRecordedCall.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(releaseRecordedCall.fulfilled, (state, action) => {
        state.loading = false;
        const call = state.recordedCalls.find(
          (c) => c.id === action.payload.recordedCallId
        );
        if (call) {
          call.retainPermanently = false;
        }
      })
      .addCase(releaseRecordedCall.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Export the actions to update filters and sorting from your UI components.
export const { setFilters, setSortModel } = recordedCallsSlice.actions;
export default recordedCallsSlice.reducer;
